import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { useMemo } from "react";

import { useAcademicsParentSideEnabled } from "../../../hooks/useAcademicsParentSideEnabled";
import type { CategoryNavLink, Translate } from "./navbarCategories";

const GRADE_LINKS = [
  ["pre-k-and-kindergarten", "Pre-Kindergarten,Kindergarten"],
  ["1st-grade", "1st grade"],
  ["2nd-grade", "2nd grade"],
  ["3rd-grade", "3rd grade"],
  ["4th-grade", "4th grade"],
  ["5th-grade", "5th grade"],
  ["6th-grade", "6th grade"],
  ["7th-grade", "7th grade"],
  ["8th-grade", "8th grade"],
  ["9th-grade", "9th grade"],
  ["10th-grade", "10th grade"],
  ["11th-grade", "11th grade"],
  ["12th-grade", "12th grade"],
] as const;

const LEARNING_NEED_LINKS = [
  {
    slug: "neurodiverse",
    label: (t: Translate) => t("Neurodiverse"),
  },
  {
    slug: "adhd",
    label: (t: Translate) => t("ADHD"),
  },
  {
    slug: "autism",
    label: (t: Translate) => t("Autism Spectrum Disorder (ASD)"),
  },
  {
    slug: "dyslexia",
    label: (t: Translate) => t("Dyslexia, Dyscalculia, or Dysgraphia"),
  },
  {
    slug: "private-speech-language",
    label: (t: Translate) => t("Speech or Language Impairment"),
    path: onlineClassesPath("private-speech-language", "popular"),
  },
  {
    slug: "gifted",
    label: (t: Translate) => t("Gifted"),
  },
] as const;

function gradeNavLinks({
  t,
  slugSuffix,
  gradeFilterSubjectSlug,
}: {
  t: Translate;
  slugSuffix: string;
  gradeFilterSubjectSlug?: string;
}): CategoryNavLink[] {
  const gradeLabels = [
    t("Pre-K & Kindergarten"),
    t("1st Grade"),
    t("2nd Grade"),
    t("3rd Grade"),
    t("4th Grade"),
    t("5th Grade"),
    t("6th Grade"),
    t("7th Grade"),
    t("8th Grade"),
    t("9th Grade"),
    t("10th Grade"),
    t("11th Grade"),
    t("12th Grade"),
  ];
  return GRADE_LINKS.map(([slug, gradeLevel], index) => ({
    nameTitleCased: gradeLabels[index],
    slug: `${slug}-${slugSuffix}`,
    ...(gradeFilterSubjectSlug
      ? {
          path: onlineClassesPath(gradeFilterSubjectSlug, undefined, {
            gradeLevel,
          }),
        }
      : {}),
  }));
}

function learningNeedsNavLinks({ t }: { t: Translate }): CategoryNavLink[] {
  return LEARNING_NEED_LINKS.map(link => ({
    nameTitleCased: link.label(t),
    slug: link.slug,
    ...("path" in link ? { path: link.path } : {}),
  }));
}

export function useBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
}: {
  electivesCategory: CategoryNavLink;
  justForFunCategory: CategoryNavLink;
}): CategoryNavLink[] {
  const { t } = useTranslation(
    "client\\components\\nav\\CategoryNavbar\\BackToSchoolNavbarCategories",
  );
  const isAcademicsParentSideEnabled = useAcademicsParentSideEnabled();

  return useMemo(
    () =>
      buildBackToSchoolNavbarCategories({
        electivesCategory,
        isAcademicsParentSideEnabled,
        justForFunCategory,
        t,
      }),
    [electivesCategory, isAcademicsParentSideEnabled, justForFunCategory, t],
  );
}

export function buildBackToSchoolNavbarCategories({
  electivesCategory,
  justForFunCategory,
  t,
}: {
  electivesCategory: CategoryNavLink;
  isAcademicsParentSideEnabled?: boolean;
  justForFunCategory: CategoryNavLink;
  t: Translate;
}): CategoryNavLink[] {
  const academicCurriculumCategory: CategoryNavLink = {
    nameTitleCased: t("Academic Curriculum"),
    slug: "full-curriculum",
    showMore: true,
    can_link: true,
    getSubcategories: () => [
      {
        nameTitleCased: t("All-subject"),
        slug: "full-curriculum",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "full-curriculum",
          }),
      },
      {
        nameTitleCased: t("English"),
        slug: "english",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english",
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math",
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science",
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies",
          }),
      },
      {
        nameTitleCased: t("Learning Methods"),
        slug: "learning-methods",
        getSubcategories: () => [
          {
            nameTitleCased: t("Project-Based"),
            slug: "project-based",
          },
          {
            nameTitleCased: t("Montessori"),
            slug: "montessori",
          },
          {
            nameTitleCased: t("Waldorf"),
            slug: "waldorf",
          },
          {
            nameTitleCased: t("Common Core (CCSS)"),
            slug: "common-core",
            path: onlineClassesPath("common-core", "popular"),
          },
          {
            nameTitleCased: t("Next Gen Science (NGSS)"),
            slug: "ngss",
            path: onlineClassesPath("ngss", "popular"),
          },
        ],
      },
    ],
  };

  const tutoringCategory: CategoryNavLink = {
    nameTitleCased: t("Tutoring"),
    slug: "tutoring",
    showMore: true,
    can_link: true,
    getSubcategories: () => [
      {
        nameTitleCased: t("English"),
        slug: "english-tutoring",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "english-tutoring",
            gradeFilterSubjectSlug: "english-tutoring",
          }),
      },
      {
        nameTitleCased: t("Math"),
        slug: "math-tutoring",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "math-tutoring",
            gradeFilterSubjectSlug: "math-tutoring",
          }),
      },
      {
        nameTitleCased: t("Science"),
        slug: "science-tutoring",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "science-tutoring",
            gradeFilterSubjectSlug: "science-tutoring",
          }),
      },
      {
        nameTitleCased: t("Social Studies"),
        slug: "social-studies-tutoring",
        getSubcategories: () =>
          gradeNavLinks({
            t,
            slugSuffix: "social-studies-tutoring",
            gradeFilterSubjectSlug: "social-studies-tutoring",
          }),
      },
      {
        nameTitleCased: t("Private Lessons"),
        slug: "private-lessons",
        getSubcategories: () => [
          {
            nameTitleCased: t("Acting & Improv"),
            slug: "acting-and-improv-lessons",
          },
          {
            nameTitleCased: t("Arts"),
            slug: "painting-and-drawing-private-lessons",
          },
          {
            nameTitleCased: t("Chess"),
            slug: "chess-private-lessons",
          },
          {
            nameTitleCased: t("Coding"),
            slug: "coding-private-lessons",
          },
          {
            nameTitleCased: t("Dance"),
            slug: "private-dance-lessons",
          },
          {
            nameTitleCased: t("Geometry"),
            slug: "geometry-tutoring",
          },
          {
            nameTitleCased: t("Music"),
            slug: "private-music-lessons",
          },
          {
            nameTitleCased: t("Photography"),
            slug: "photography-private-lessons",
          },
          {
            nameTitleCased: t("Public Speaking"),
            slug: "public-speaking-private-lessons",
          },
          {
            nameTitleCased: t("Reading"),
            slug: "reading-tutoring",
          },
          {
            nameTitleCased: t("Writing"),
            slug: "writing-tutoring",
          },
        ],
      },
      {
        nameTitleCased: t("1-on-1 Coaching"),
        slug: "private-coaching",
        getSubcategories: () => [
          {
            nameTitleCased: t("Executive Function"),
            slug: "executive-function-tutoring",
          },
          {
            nameTitleCased: t("Study Skills"),
            slug: "study-skills-tutoring",
          },
          {
            nameTitleCased: t("Social Skills"),
            slug: "social-skills-coaching",
          },
          {
            nameTitleCased: t("Phonics & Early Reading"),
            slug: "phonics-tutoring",
          },
        ],
      },
      {
        nameTitleCased: t("World Languages"),
        slug: "languages-tutoring",
        getSubcategories: () => [
          {
            nameTitleCased: t("Spanish"),
            slug: "spanish-tutoring",
          },
          {
            nameTitleCased: t("French"),
            slug: "french-tutoring",
          },
          {
            nameTitleCased: t("Korean"),
            slug: "korean-tutoring",
          },
          {
            nameTitleCased: t("All"),
            slug: "languages-tutoring",
          },
        ],
      },
      {
        nameTitleCased: t("Test Prep"),
        slug: "test-prep-tutoring",
        getSubcategories: () => [
          {
            nameTitleCased: t("State Test Prep"),
            slug: "state-test-prep-tutoring",
            path: onlineClassesPath("test-prep-tutoring"),
          },
          {
            nameTitleCased: t("AP Test Prep"),
            slug: "ap-test-prep-tutoring",
          },
          {
            nameTitleCased: t("SAT/ACT"),
            slug: "sat-tutoring",
            path: onlineClassesPath("sat-tutoring", "popular"),
          },
          {
            nameTitleCased: t("All"),
            slug: "test-prep-tutoring",
          },
        ],
      },
    ],
  };

  const electivesAndEnrichmentCategory: CategoryNavLink = {
    ...electivesCategory,
    nameTitleCased: t("Electives & Enrichment"),
  };

  const socialClubsCategory: CategoryNavLink = {
    ...justForFunCategory,
    nameTitleCased: t("Social Clubs"),
    slug: "social-clubs",
    path: onlineClassesPath("social-clubs", "popular"),
  };

  const learningNeedsCategory: CategoryNavLink = {
    nameTitleCased: t("Learning Needs"),
    slug: "learning-needs",
    showMore: true,
    can_link: true,
    renderAsFlatList: true,
    getSubcategories: () => learningNeedsNavLinks({ t }),
  };

  return [
    academicCurriculumCategory,
    tutoringCategory,
    electivesAndEnrichmentCategory,
    socialClubsCategory,
    learningNeedsCategory,
  ];
}
